import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { VictoryArea } from 'victory-area/es'
import { VictoryLine } from 'victory-line/es'
import { VictoryChart } from 'victory-chart/es'
import { VictoryAxis } from 'victory-axis/es'
import { VictoryLabel } from 'victory-core/es'
import { VictoryScatter } from 'victory-scatter/es'
import { VictoryTooltip } from 'victory-tooltip/es'
import { VictoryVoronoiContainer } from 'victory-voronoi-container/es'
import max from 'lodash/max'
import min from 'lodash/min'
import get from 'lodash/get'
import { datadogLogs } from '@datadog/browser-logs'

import Alert from '../Alert'
import Button from '../Button'

import { MOON_GRAY, NAVY } from '../config/colors'
import { dateString } from '../helpers/propTypes'
import { formatUnit } from '../helpers/units'
import { formatNumber } from '../helpers/numbers'
import { ReactComponent as ChevronIcon } from '../graphics/chevron-right.svg'

const THRESHOLD_COLORS = {
  ok: {
    // .green
    stroke: '#0BB66F',
    // .washed-green
    fill: '#DDF3EA',
  },
  warning: {
    // .yellow
    stroke: '#FFB700',
    // .washed-yellow
    fill: '#FFFBD8',
  },
  alert: {
    // .red
    stroke: '#C81349',
    // .washed-red
    fill: '#FFDFDF',
  },
}

/* Inner "dumb" component --------------------------------------------------- */
const LineChart = React.memo(function LineChart({
  thresholds = [],
  points = [],
  unit,
  fromDate,
  toDate,
}) {
  const hasNoData = points.length === 0
  const pointsYValues = points.map((p) => p.value)
  const pointsYMax = hasNoData ? 7 : Math.ceil(max(pointsYValues))
  const pointsYMin = hasNoData ? 0 : Math.floor(min(pointsYValues))
  const formattedUnit = formatUnit(unit)
  return (
    <div className="relative aspect-ratio--6x4">
      {/* Allow the data to define the Y axis domain, but set a fallback if
      there are no datapoints (instead of the default [0, 1] Y axis domain) */}
      <VictoryChart
        containerComponent={<VictoryVoronoiContainer />}
        width={450}
        height={300}
        domain={{
          x: [new Date(fromDate), new Date(toDate)],
          y: [Math.min(0, pointsYMin), Math.max(7, pointsYMax)],
        }}
        style={{
          parent: {
            position: 'absolute',
          },
        }}
      >
        {/* Fill in chart background with threshold colors */}
        {thresholds.map(({ from, to, name }) => {
          // The top-level threshold is open-ended, which means that `to` is null.
          // To always show it, we will set the top level as the max point + 2
          const y = to ?? Math.max(from, pointsYMax) + 2
          // If the datapoints contain negative Y axis values, the lowest
          // threshold should extend beyond a defined `from` point of 0
          const y0 = from === 0 ? min([from, pointsYMin]) : from
          return (
            <VictoryArea
              animate={{ duration: 600, onLoad: { duration: 0 } }}
              key={name}
              data={[
                {
                  x: new Date(fromDate),
                  y,
                  y0,
                },
                {
                  x: new Date(toDate),
                  y,
                  y0,
                },
              ]}
              style={{
                data: {
                  fill: get(THRESHOLD_COLORS, [name, 'fill'], 'none'),
                },
              }}
            />
          )
        })}

        {/* X axis */}
        <VictoryAxis
          tickCount={3}
          tickFormat={(t) => dayjs(t).format('DD.MM.YY')}
          style={{
            axis: {
              stroke: MOON_GRAY,
              strokeWidth: 0.5,
            },
            tickLabels: {
              fill: MOON_GRAY,
              fontWeight: 300,
            },
          }}
        />

        {/* Y axis */}
        <VictoryAxis
          animate={{ duration: 600, onLoad: { duration: 0 } }}
          dependentAxis
          label={formattedUnit}
          tickFormat={(t) => Math.round(t)}
          style={{
            axis: {
              stroke: MOON_GRAY,
              strokeWidth: 0.5,
            },
            grid: {
              stroke: ({ tickValue }) => {
                if (!points || hasNoData) {
                  return 'none'
                } else if (!thresholds || thresholds.length === 0) {
                  return MOON_GRAY
                } else if (
                  tickValue >
                  get(
                    thresholds.find((t) => t.name === 'alert'),
                    'from'
                  )
                ) {
                  return THRESHOLD_COLORS.alert.stroke
                } else if (
                  tickValue >
                  get(
                    thresholds.find((t) => t.name === 'warning'),
                    'from'
                  )
                ) {
                  return THRESHOLD_COLORS.warning.stroke
                } else {
                  return THRESHOLD_COLORS.ok.stroke
                }
              },
              strokeWidth: 0.5,
            },
            axisLabel: {
              fill: MOON_GRAY,
              padding: 38,
            },
            tickLabels: {
              fill: MOON_GRAY,
              fontWeight: 300,
              padding: 5,
            },
          }}
        />

        {/* Placeholder label */}
        {hasNoData && (
          <VictoryLabel
            x={200}
            y={150}
            text="Ingen data"
            style={{ fill: MOON_GRAY }}
          />
        )}

        {/* Chart the actual data */}
        <VictoryLine
          animate={{ duration: 600, onLoad: { duration: 0 } }}
          data={points}
          x={(d) => new Date(d.time)}
          y="value"
          style={{
            data: {
              strokeWidth: 1.25,
              stroke: NAVY,
            },
          }}
        />
        <VictoryScatter
          data={points}
          x={(d) => new Date(d.time)}
          y="value"
          style={{
            data: {
              fill: NAVY,
              fillOpacity: ({ active }) => (active ? 1 : 0),
            },
            labels: {
              fill: '#fff',
            },
          }}
          labels={({ datum }) => [
            `${formatNumber(datum.value, 2)}${formattedUnit}`,
            `${dayjs(datum.time).format('DD.MM.YY')}`,
          ]}
          labelComponent={
            <VictoryTooltip
              cornerRadius={2}
              flyoutStyle={{
                stroke: 'none',
                fill: 'rgba(0, 0, 0, 0.7)',
              }}
            />
          }
        />
      </VictoryChart>
    </div>
  )
})

LineChart.propTypes = {
  thresholds: PropTypes.arrayOf(
    PropTypes.shape({
      from: PropTypes.number,
      to: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  points: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      time: dateString,
    })
  ),
  fromDate: dateString,
  toDate: dateString,
}

/* Wrapping "smart" component ----------------------------------------------- */
export default function LineChartContainer({
  roomId,
  roomName,
  fromDate,
  toDate,
  prevRoom,
  nextRoom,
  setSelectedRoom,
  fetchReportFunction,
}) {
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState()
  const [points, setPoints] = useState([])
  const [thresholds, setThresholds] = useState([])
  const [unit, setUnit] = useState()

  useEffect(() => {
    async function _loadData() {
      setLoading(true)
      setErrorMessage(null)
      try {
        const { points, thresholds, unit } = await fetchReportFunction({
          roomId,
          fromDate,
          toDate,
        })
        setPoints(points)
        setThresholds(thresholds)
        setUnit(unit)
      } catch (error) {
        setErrorMessage(error.message)
        setPoints([])
        setThresholds([])
        datadogLogs.logger.log(
          `LineChartContainer._loadData: ${error.message}`,
          {},
          /No data for that period/.test(error.message) ? 'warn' : 'error'
        )
      } finally {
        setLoading(false)
      }
    }

    const validDateRange = toDate >= fromDate

    if (roomId && validDateRange) {
      _loadData()
    } else {
      setErrorMessage('Den valgte periode er ugyldig')
    }
  }, [roomId, fromDate, toDate, fetchReportFunction])

  return (
    <div className={loading ? 'o-50' : null}>
      <div className="flex flex-row items-center justify-center">
        {prevRoom ? (
          <Button
            size="small"
            variant="link"
            onClick={() => setSelectedRoom(prevRoom.id)}
            title={prevRoom.name}
          >
            <ChevronIcon width="6" height="10" className="rotate-180" />
          </Button>
        ) : (
          <ChevronIcon
            width="6"
            height="10"
            className="light-silver rotate-180 pv1 ph2"
          />
        )}
        <h5 className="mv0 f6 tc navy mh2">{roomName}</h5>
        {nextRoom ? (
          <Button
            size="small"
            variant="link"
            onClick={() => setSelectedRoom(nextRoom.id)}
            title={nextRoom.name}
          >
            <ChevronIcon width="6" height="10" />
          </Button>
        ) : (
          <ChevronIcon width="6" height="10" className="light-silver pv1 ph2" />
        )}
      </div>
      <LineChart
        points={points}
        thresholds={thresholds}
        unit={unit}
        fromDate={fromDate}
        toDate={toDate}
      />
      {errorMessage && (
        <Alert type="error" className="mb3 mh4">
          {errorMessage}
        </Alert>
      )}
    </div>
  )
}

LineChartContainer.propTypes = {
  roomId: PropTypes.string.isRequired,
  roomName: PropTypes.string.isRequired,
  fromDate: dateString,
  toDate: dateString,
  prevRoom: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  nextRoom: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  setSelectedRoom: PropTypes.func,
  fetchReportFunction: PropTypes.func.isRequired,
}
